import type { PropsWithChildren, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';
import { useSetting } from '../lib/settings';
import { getRootUrl } from '../lib/urls';

interface ShellProps {
  showBack?: boolean;
  backTo?: string;
  className?: string;
  rightSlot?: ReactNode;
  showFooter?: boolean;
  heroVideo?: boolean;
}

export function Shell({
  children,
  showBack,
  backTo = '/',
  className,
  rightSlot,
  showFooter,
  heroVideo,
}: PropsWithChildren<ShellProps>) {
  const { t } = useTranslation();
  const brandName = useSetting('brand.name');
  const logoUrl = useSetting('brand.logoUrl');
  return (
    <div className="min-h-full flex flex-col bg-sand">
      <header className={cn(
        heroVideo ? 'absolute top-0 inset-x-0 z-20' : 'sticky top-0 z-10 bg-sand/90 backdrop-blur-md',
      )}>
        <div className="max-w-md mx-auto flex items-center justify-between px-5 h-16 gap-2">
          <Link to="/" className="flex items-center gap-2.5 group flex-none">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className={cn('h-8 w-auto object-contain', heroVideo && 'brightness-0 invert drop-shadow')} />
            ) : (
              <BrandMark />
            )}
            <span className={cn('font-black text-xl tracking-tight', heroVideo ? 'text-white drop-shadow' : 'text-oud-900')}>
              {brandName}
            </span>
          </Link>

          <div className="flex items-center gap-2">
            {rightSlot}
            {showBack ? (
              <Link
                to={backTo}
                className={cn(
                  'flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 font-bold transition-colors',
                  heroVideo
                    ? 'text-white/90 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20'
                    : 'text-oud-700 hover:text-oud-900 bg-cream border border-oud-100/60',
                )}
              >
                <span>{t('common.back')}</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-3.5 h-3.5"
                  aria-hidden
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </Link>
            ) : null}
          </div>
        </div>
        {heroVideo ? null : <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-l from-transparent via-gold/40 to-transparent" />}
      </header>
      <main className={cn('flex-1 max-w-md mx-auto w-full px-5 pb-24', className)}>
        {children}
      </main>
      {showFooter ? (
        <footer className="py-4 text-center">
          <a
            href={getRootUrl()}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-[11px] text-oud-400 hover:text-oud-700 transition-colors"
          >
            <span>تصميم</span>
            <span className="font-black text-oud-600">سنديان</span>
            <span>♥</span>
          </a>
        </footer>
      ) : null}
    </div>
  );
}

function BrandMark() {
  return (
    <span className="relative flex items-center justify-center w-8 h-8 rounded-xl bg-gradient-to-br from-oud-800 to-oud-900 shadow-soft overflow-hidden">
      <span className="absolute inset-0 najdi-pattern opacity-30" aria-hidden />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="#c9904e"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="relative w-4 h-4"
        aria-hidden
      >
        <path d="M5 10V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3" />
        <path d="M3 10h18v4H3z" />
        <path d="M6 14v5M18 14v5" />
      </svg>
    </span>
  );
}
